import { useEffect, type ReactNode } from 'react';
import clsx from 'clsx';

import { CloseIcon } from './Icon';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Bottom sheet. Tap scrim or close button to dismiss; Esc on keyboard.
 * Respects `prefers-reduced-motion` via CSS keyframes.
 */
export function Sheet({ open, onClose, title, children, className }: Props) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        className="pb-scrim absolute inset-0 bg-black/55"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'sheet'}
        className={clsx(
          'pb-sheet-enter absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-[28px] bg-pb-paper shadow-pb-lg safe-area-bottom',
          className,
        )}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 bg-pb-paper/95 px-5 pt-5 pb-3 backdrop-blur">
          <div>
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-pb-border" />
            {title ? (
              <h2 className="pb-display text-[20px]">{title}</h2>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            className="pb-focus-ring -mt-1 -mr-2 rounded-full p-2 text-tg-hint hover:text-tg-text"
          >
            <CloseIcon size={20} />
          </button>
        </div>
        <div className="px-5 pb-6">{children}</div>
      </div>
    </div>
  );
}
